import Pubnub, { HistoryParameters } from "pubnub";
import { usePubNub } from "pubnub-react";
import { createContext, FC, useContext, useEffect, useState } from "react";
import {
  useGetAllChatChannelsQuery,
  useGetSMSCconversationQuery,
  useGetSMSUnreadCountQuery,
  useSMSUsersQuery,
} from "../../../api/network/chatApiServices";
import {
  ChatChannel,
  ChatConversationPresence,
  SMSMessage,
  SMSUnreadCountResponse,
  SMSUser,
} from "../../../api/types/Chat";
import { ListingResponse } from "../../../api/types/Listing";
import { APP_CONFIG } from "../../../constants/constants";
import {
  TPubNubConversationMessage,
  TPubNubFile,
  TPubNubNewFileEvent,
  TPubNubNewMessageEvent,
  TPubNubTextMessage,
} from "./types/pubnubChat";
import { pubnubTimetokenToJsTime } from "./util/chat";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";

type TConversations = {
  [key: string]: TPubNubConversationMessage[];
};

const contextDefaultValue = {
  subscribedChannels: [] as ChatChannel[], // initially TPubNubChannel[]
  setSubscribedChannels: (value: ChatChannel[]) => {}, // initially TPubNubChannel[]
  selectedChatChannel: "",
  setSelectedChatChannel: (value: string) => {},
  chatUsers: [] as any[],
  setChatUsers: (value: any) => {},
  channelTransitionInProgress: false,
  setChannelTransitionInProgress: (value: boolean) => {},
  conversations: {} as TConversations,
  setConversations: (value: TConversations) => {},
  presence: {} as ChatConversationPresence,
  addPresence: (value: any) => {},
  fileBeingUploaded: null,
  setFileBeingUploaded: (value: any) => {},

  messagingMode: "chat" as "chat" | "sms",
  setMessagingMode: (value: "chat" | "sms") => {},
  dataSMSUsers: {
    count: 0,
    next: "",
    previous: "",
    results: [],
  } as ListingResponse<SMSUser[]>,
  isFetchingSMSUsers: false,
  dataSMSUnreadCount: { unread_count: 0 } as SMSUnreadCountResponse,
  isFetchingSMSUnreadCount: false,
  refetchSMSUnreadCount: () => {},
  dataSMSConversation: {
    count: 0,
    next: "",
    previous: "",
    results: [],
  } as ListingResponse<SMSMessage[]>,
  isFetchingSMSConversation: false,
  refetchSMSConversation: () => {},
  selectedSMSUser: "",
  setSelectedSMSUser: (value: string) => {},
};

const chatContext = createContext(contextDefaultValue);

export const ChatContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();

  const [subscribedChannels, setSubscribedChannels] = useState<ChatChannel[]>(
    []
  ); // initially TPubNubChannel[]
  const [selectedChatChannel, setSelectedChatChannel] = useState<string>(""); // initially 'awesome-channel'
  const [chatUsers, setChatUsers] = useState<any[]>([]);
  const [channelTransitionInProgress, setChannelTransitionInProgress] =
    useState<boolean>(false);
  const [conversations, setConversations] = useState<TConversations>({});
  const [presence, addPresence] = useState<any>(
    getLocalStorageItem(localStorageKeys.chatPresence) || {}
  );
  const [fileBeingUploaded, setFileBeingUploaded] = useState<any>(null);

  const [messagingMode, setMessagingMode] = useState<"chat" | "sms">(
    contextDefaultValue.messagingMode
  );

  const pubnub = usePubNub();

  // get list of channels from backend
  const { data: dataChatChannels, isFetching: isFetchingChatChannels } =
    useGetAllChatChannelsQuery();

  useEffect(() => {
    if (!!dataChatChannels && !isFetchingChatChannels) {
      const channels = dataChatChannels?.results || [];
      setSubscribedChannels(channels);
    }
  }, [dataChatChannels, isFetchingChatChannels]);

  // update message history when the channel changes
  useEffect(() => {
    const fetchHistorySequentially = async () => {
      for (const channel of subscribedChannels) {
        try {
          const response = await pubnub.history({
            channel: channel.channel_id,
          } as HistoryParameters);
          const messages = response.messages as TPubNubConversationMessage[];
          setConversations((prevConversations) => ({
            ...prevConversations,
            [channel.channel_id]: messages,
          }));
        } catch (error) {
          console.error(
            `[PUBNUB] Error fetching '${channel.display_info.name}' history:`,
            error
          );
        }
      }
    };

    fetchHistorySequentially();
  }, [pubnub, subscribedChannels]);

  // get unread messages count
  useEffect(() => {
    // calculate unread messages count from subscribedChannels and lastReadMessageTimestamp
    const calculatedUnreadMessagesCount = () => {
      // TODO: NEEDS FIXING
      const newPresence = { ...presence };
      for (const channel of subscribedChannels) {
        newPresence[channel.channel_id] = {
          lastReadMessageTimestamp:
            presence?.[channel.channel_id]?.lastReadMessageTimestamp || 0,
          unreadMessageCount:
            conversations?.[channel.channel_id]
              ?.filter(
                (message) =>
                  (message.entry as TPubNubTextMessage)?.sender !==
                    pubnub.getUUID() &&
                  (message.entry as TPubNubFile)?.message?.sender !==
                    pubnub.getUUID()
              )
              ?.filter((message) => {
                const messageTime = pubnubTimetokenToJsTime(message.timetoken);
                const lastReadTime =
                  presence?.[channel.channel_id]?.lastReadMessageTimestamp || 0;
                return messageTime > lastReadTime;
              }).length || 0,
        };
      }
      addPresence(newPresence);
    };

    // get unread messages count using pubnub.messageCounts
    // const fetchUnreadMessagesCount = async () => {
    //   try {
    //     const response = await pubnub.messageCounts({
    //       channels: subscribedChannels.map(channel => channel.channel_id),
    //       channelTimetokens:
    //         subscribedChannels
    //           .map(
    //             channel =>
    //               presence?.[channel.channel_id]?.lastReadMessageTimestamp || 0
    //           ),
    //     });
    //     console.log('UNREAD response:', response);
    //     const newPresence = { ...presence };
    //     for (const channel of subscribedChannels) {
    //       const unreadMessageCount = response.channels[channel.channel_id] || 0;
    //       newPresence[channel.channel_id] = {
    //         lastReadMessageTimestamp: presence?.[channel.channel_id]?.lastReadMessageTimestamp || 0,
    //         unreadMessageCount,
    //       };
    //     }
    //     addPresence(newPresence);
    //   } catch (error) {
    //     console.error(`[PUBNUB] Error fetching unread messages count:`, error);
    //   }
    // };

    // fetchUnreadMessagesCount();
    calculatedUnreadMessagesCount();
  }, [pubnub, subscribedChannels, conversations]);

  // save presence to loaclStorage
  useEffect(() => {
    setLocalStorageItem(localStorageKeys.chatPresence, presence);
  }, [presence]);

  // handle new messages from the channel
  const handleMessage = (event: TPubNubNewMessageEvent) => {
    // : Pubnub.MessageEvent
    if (APP_CONFIG.DEBUG.PUBNUB) console.log("[PUBNUB] New Message:", event);
    const message = event.message;
    if (typeof message === "string" || message.hasOwnProperty("message")) {
      console.log("message:", message);
      console.log("conversations:", conversations);
      setConversations((conversations) => ({
        ...conversations,
        [event.channel]: [
          ...(conversations[event.channel] || []),
          {
            timetoken: parseInt(event.timetoken),
            entry: message,
          },
        ],
      }));
      // if conversation is not active and message is sent by someone other
      // than the current user, update unread message count
      if (
        selectedChatChannel !== event.channel &&
        event.publisher !== pubnub.getUUID()
      ) {
        if (APP_CONFIG.DEBUG.MESSAGING)
          console.log(
            "New Message Event Publisher",
            event.publisher,
            "is not",
            pubnub.getUUID()
          );
        addPresence((prevPresence: ChatConversationPresence) => ({
          ...prevPresence,
          [event.channel]: {
            lastReadMessageTimestamp:
              prevPresence?.[event.channel]?.lastReadMessageTimestamp || 0,
            unreadMessageCount:
              (prevPresence?.[event.channel]?.unreadMessageCount || 0) + 1,
          },
        }));
      }
    }
  };

  // handle new file from the channel
  const handleFile = (event: TPubNubNewFileEvent) => {
    // : Pubnub.MessageEvent
    if (APP_CONFIG.DEBUG.PUBNUB) console.log("[PUBNUB] New File:", event);
    const message = event.message;
    const file = event.file;
    if (typeof message === "string" || message.hasOwnProperty("message")) {
      console.log("message:", message);
      console.log("conversations:", conversations);
      setConversations((conversations) => ({
        ...conversations,
        [event.channel]: [
          ...(conversations[event.channel] || []),
          {
            timetoken: parseInt(event.timetoken),
            entry: {
              message,
              file,
            },
          },
        ],
      }));
      // if conversation is not active and message is sent by someone other
      // than the current user, update unread message count
      if (
        event.channel !== selectedChatChannel &&
        event.publisher !== pubnub.getUUID()
      ) {
        addPresence((prevPresence: ChatConversationPresence) => ({
          ...prevPresence,
          [event.channel]: {
            lastReadMessageTimestamp:
              prevPresence?.[event.channel]?.lastReadMessageTimestamp || 0,
            unreadMessageCount:
              (prevPresence?.[event.channel]?.unreadMessageCount || 0) + 1,
          },
        }));
      }
    }
  };

  const handlePresence = (event: Pubnub.PresenceEvent) => {
    const { state, channel, action } = event;

    if (action === "state-change") {
      addPresence((presence: any) => ({
        ...(presence || {}),
        [channel]: state,
      }));
      // {
      //   presence: { lastReadMessageTimestamp: state.lastReadMessageTimestamp },
      //   id: channel,
      // }
    }
  };

  const pubnubListener = {
    message: handleMessage,
    presence: handlePresence,
    file: handleFile,
    status: (status: Pubnub.StatusEvent) => {
      if (APP_CONFIG.DEBUG.PUBNUB) console.log("[PUBNUB] Status:", status);
    },
  };

  // subscribe to channels
  useEffect(() => {
    pubnub.addListener(pubnubListener);
    pubnub.subscribe({
      channels: subscribedChannels.map((channel) => channel.channel_id),
      withPresence: true,
    });
    return () => {
      pubnub.removeListener(pubnubListener);
      pubnub.unsubscribeAll();
    };
  }, [pubnub, subscribedChannels]);

  // get list of subscribed channels - deprecated as anyone can access others channels
  // useEffect(() => {
  //   const channels: string[] = pubnub.getSubscribedChannels();
  //   if (APP_CONFIG.DEBUG.PUBNUB) console.log('[PUBNUB] Channels:', channels);
  //   // get details of each channel and set all data to subscribedChannels
  //   pubnub
  //     .hereNow({ includeUUIDs: true, channels })
  //     .then(response => {
  //       const channels = response.channels;
  //       const channelsArray = Object.keys(channels).map(channel => channels[channel] as TPubNubChannel);
  //       if (APP_CONFIG.DEBUG.PUBNUB) console.log('[PUBNUB] Channel Details:', channelsArray);
  //       setSubscribedChannels(channelsArray);
  //     })
  //     .catch(error => {
  //       if (APP_CONFIG.DEBUG.PUBNUB) console.error('[PUBNUB] Error fetching channel details:', error);
  //       setSubscribedChannels(channels.map(channel => ({
  //         name: channel,
  //         occupants: [],
  //         occupancy: 0
  //       })));
  //     });
  // }, [pubnub]);

  // get list of channels subscribed - USES "PRESENCE" API
  // useEffect(() => {
  //   pubnub
  //     .hereNow({ includeUUIDs: true, channels: subscribedChannels.map(channel => channel.name) })
  //     .then(response => {
  //       const channels = response.channels;
  //       const channelsArray = Object.keys(channels).map(channel => channels[channel] as TPubNubChannel);
  //       setSubscribedChannels(channelsArray);
  //     });
  // }, [pubnub, subscribedChannels]);

  // SMS Mode
  const { getUserACLAccessLevel } = useACL();
  const { data: dataSMSUsers, isFetching: isFetchingSMSUsers } =
    useSMSUsersQuery(
      {},
      {
        skip:
          getUserACLAccessLevel(ACL_NAMES.ACL_MESSAGING_SEND_MSG) <
          ROLE_ACCESS_LEVELS.READ,
      }
    );

  const [selectedSMSUser, setSelectedSMSUser] = useState<string>("");

  const {
    data: dataSMSUnreadCount,
    isFetching: isFetchingSMSUnreadCount,
    refetch: refetchSMSUnreadCount,
  } = useGetSMSUnreadCountQuery();

  const {
    data: dataSMSConversation,
    isFetching: isFetchingSMSConversation,
    refetch: refetchSMSConversation,
  } = useGetSMSCconversationQuery(
    {
      driverid: selectedSMSUser,
    },
    { skip: !selectedSMSUser }
  );

  // refetch unread count after sms conversation is fetched
  useEffect(() => {
    if (!!dataSMSConversation && !isFetchingSMSConversation) {
      refetchSMSUnreadCount();
    }
  }, [dataSMSConversation, isFetchingSMSConversation, refetchSMSUnreadCount]);

  return (
    <chatContext.Provider
      value={{
        subscribedChannels,
        setSubscribedChannels,
        selectedChatChannel,
        setSelectedChatChannel,
        chatUsers,
        setChatUsers,
        channelTransitionInProgress,
        setChannelTransitionInProgress,
        conversations,
        setConversations,
        presence,
        addPresence,
        fileBeingUploaded,
        setFileBeingUploaded,

        messagingMode,
        setMessagingMode,
        dataSMSUsers: dataSMSUsers as ListingResponse<SMSUser[]>,
        isFetchingSMSUsers,
        dataSMSUnreadCount: dataSMSUnreadCount as SMSUnreadCountResponse,
        isFetchingSMSUnreadCount,
        refetchSMSUnreadCount,
        dataSMSConversation: dataSMSConversation as ListingResponse<
          SMSMessage[]
        >,
        isFetchingSMSConversation,
        refetchSMSConversation,
        selectedSMSUser,
        setSelectedSMSUser,
      }}
    >
      {children}
    </chatContext.Provider>
  );
};

export const useChatContext = () => {
  const {
    subscribedChannels,
    setSubscribedChannels,
    selectedChatChannel,
    setSelectedChatChannel,
    chatUsers,
    setChatUsers,
    channelTransitionInProgress,
    setChannelTransitionInProgress,
    conversations,
    setConversations,
    presence,
    addPresence,
    fileBeingUploaded,
    setFileBeingUploaded,

    messagingMode,
    setMessagingMode,
    dataSMSUsers,
    isFetchingSMSUsers,
    dataSMSUnreadCount,
    isFetchingSMSUnreadCount,
    refetchSMSUnreadCount,
    dataSMSConversation,
    isFetchingSMSConversation,
    refetchSMSConversation,
    selectedSMSUser,
    setSelectedSMSUser,
  } = useContext(chatContext);
  return {
    subscribedChannels,
    setSubscribedChannels,
    selectedChatChannel,
    setSelectedChatChannel,
    chatUsers,
    setChatUsers,
    channelTransitionInProgress,
    setChannelTransitionInProgress,
    conversations,
    setConversations,
    presence,
    addPresence,
    fileBeingUploaded,
    setFileBeingUploaded,

    messagingMode,
    setMessagingMode,
    dataSMSUsers,
    isFetchingSMSUsers,
    dataSMSUnreadCount,
    isFetchingSMSUnreadCount,
    refetchSMSUnreadCount,
    dataSMSConversation,
    isFetchingSMSConversation,
    refetchSMSConversation,
    selectedSMSUser,
    setSelectedSMSUser,
  };
};
